import { Bot, Check, Clock3, MessageCircle, MessageSquarePlus, Send, ShieldAlert, Sparkles, User2, X } from 'lucide-react';
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../i18n/LanguageContext';
import {
  SentinelActionProposal,
  SentinelContextEnvelope,
  SentinelConversation,
  SentinelMessage,
  SentinelSelectedEntities,
} from './types';

type SentinelDrawerProps = {
  open: boolean;
  onClose: () => void;
  conversations: SentinelConversation[];
  activeConversationId: string | null;
  onSelectConversation: (conversationId: string) => void;
  onNewChat: () => Promise<void>;
  messages: SentinelMessage[];
  proposals: SentinelActionProposal[];
  onConfirmProposal: (proposalId: string) => Promise<void>;
  onCancelProposal: (proposalId: string) => Promise<void>;
  onSend: (value: string) => Promise<void>;
  sending: boolean;
  loading: boolean;
  currentContextHint: {
    route: string;
    module: string;
    selected_entities: SentinelSelectedEntities;
    tenant_id: string | null;
    user_role: string | null;
  };
  currentEnvelope: SentinelContextEnvelope | null;
};

const sentinelItOverrides: Record<string, string> = {
  'Sentinel Chat': 'Chat Sentinel',
  'New conversation': 'Nuova conversazione',
  'New chat': 'Nuova chat',
  'Close Sentinel': 'Chiudi Sentinel',
  'role-unavailable': 'ruolo-non-disponibile',
  'tenant-unavailable': 'tenant-non-disponibile',
  'Syncing...': 'Sincronizzazione...',
  'Start a conversation': 'Avvia una conversazione',
  'Ask for chart summaries, action proposals, scheduling support, or administrative guidance based on your current context.':
    'Chiedi riepiloghi clinici, proposte di azione, supporto alla programmazione o guida amministrativa in base al contesto corrente.',
  'Thinking...': 'Sto pensando...',
  'Proposed Action': 'Azione proposta',
  Confirm: 'Conferma',
  Cancel: 'Annulla',
  'Ask Sentinel anything...': 'Chiedi qualsiasi cosa a Sentinel...',
  'Sentinel proposes actions first and executes only after confirmation.':
    'Sentinel propone prima le azioni ed esegue solo dopo conferma.',
  'Start new chat': 'Avvia nuova chat',
  'Insert current context': 'Inserisci contesto corrente',
  'List Sentinel capabilities': 'Elenca le capacità di Sentinel',
  'Propose CREATE_NOTE for selected patient': 'Proponi CREATE_NOTE per il paziente selezionato',
  'Propose ORDER_LABS for selected patient': 'Proponi ORDER_LABS per il paziente selezionato',
  'Propose CREATE_TASK linked to current context': 'Proponi CREATE_TASK collegato al contesto corrente',
  'Propose CANCEL_APPOINTMENT for selected appointment': 'Proponi CANCEL_APPOINTMENT per l’appuntamento selezionato',
  'Propose feature flag update': 'Proponi aggiornamento feature flag',
  'Propose audit export': 'Proponi esportazione audit',
  User: 'Utente',
  Assistant: 'Assistente',
  System: 'Sistema',
};

function createSentinelTranslator(language: string, tr: (value: string) => string) {
  if (language !== 'it') return tr;
  return (phrase: string) => {
    const normalized = phrase.trim();
    if (!normalized) return phrase;
    const globalTranslation = tr(normalized);
    if (globalTranslation !== normalized) return phrase.replace(normalized, globalTranslation);
    const local = sentinelItOverrides[normalized];
    return local ? phrase.replace(normalized, local) : phrase;
  };
}

function entityChips(selected: SentinelSelectedEntities) {
  return Object.entries(selected)
    .filter(([, value]) => Boolean(value))
    .map(([key, value]) => `${key}: ${value}`);
}

export default function SentinelDrawer(props: SentinelDrawerProps) {
  const [composer, setComposer] = useState('');
  const [commandFilter, setCommandFilter] = useState('');
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const { language, tr } = useI18n();
  const ts = createSentinelTranslator(language, tr);

  const effectiveContext = props.currentEnvelope
    ? {
        route: props.currentEnvelope.route || props.currentContextHint.route,
        module: props.currentEnvelope.module,
        selected_entities: props.currentEnvelope.selected_entities,
        tenant_id: props.currentEnvelope.tenant_id,
        user_role: props.currentEnvelope.user_role,
      }
    : props.currentContextHint;

  const commands = useMemo(() => {
    const base = [
      { command: '/new', hint: ts('Start new chat') },
      { command: '/context', hint: ts('Insert current context') },
      { command: '/help', hint: ts('List Sentinel capabilities') },
    ];
    if (effectiveContext.selected_entities.patient_id) {
      base.push({ command: '/patient-note', hint: ts('Propose CREATE_NOTE for selected patient') });
      base.push({ command: '/order-labs', hint: ts('Propose ORDER_LABS for selected patient') });
      base.push({ command: '/create-task', hint: ts('Propose CREATE_TASK linked to current context') });
    }
    if (effectiveContext.selected_entities.appointment_id) {
      base.push({ command: '/cancel-appointment', hint: ts('Propose CANCEL_APPOINTMENT for selected appointment') });
    }
    if (effectiveContext.module === 'Admin') {
      base.push({ command: '/flags', hint: ts('Propose feature flag update') });
      base.push({ command: '/audit-export', hint: ts('Propose audit export') });
    }
    return base;
  }, [effectiveContext.module, effectiveContext.selected_entities.appointment_id, effectiveContext.selected_entities.patient_id, ts]);

  const filteredCommands = useMemo(() => {
    if (!commandFilter.trim()) return commands;
    const value = commandFilter.toLowerCase();
    return commands.filter((item) => item.command.includes(value));
  }, [commandFilter, commands]);

  useEffect(() => {
    if (!props.open) return;
    const timer = window.setTimeout(() => composerRef.current?.focus(), 40);
    return () => window.clearTimeout(timer);
  }, [props.open]);

  async function submitComposer(event: FormEvent) {
    event.preventDefault();
    const next = composer.trim();
    if (!next || props.sending) return;
    if (next === '/new') {
      setComposer('');
      setCommandFilter('');
      await props.onNewChat();
      return;
    }
    if (next === '/context') {
      setComposer(JSON.stringify(effectiveContext, null, 2));
      setCommandFilter('');
      return;
    }
    if (next === '/help') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Show available capabilities for this context and role.');
      return;
    }
    if (next === '/patient-note') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose a CREATE_NOTE action for the selected patient.');
      return;
    }
    if (next === '/order-labs') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose an ORDER_LABS action for the selected patient.');
      return;
    }
    if (next === '/create-task') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose a CREATE_TASK action linked to the current context.');
      return;
    }
    if (next === '/cancel-appointment') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose a CANCEL_APPOINTMENT action for the selected appointment.');
      return;
    }
    if (next === '/flags') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose UPDATE_TENANT_FEATURE_FLAGS with your recommended values.');
      return;
    }
    if (next === '/audit-export') {
      setComposer('');
      setCommandFilter('');
      await props.onSend('Propose EXPORT_AUDIT_LOGS for the last 7 days.');
      return;
    }

    setComposer('');
    setCommandFilter('');
    await props.onSend(next);
  }

  if (!props.open) return null;

  const selectedEntityChips = entityChips(effectiveContext.selected_entities);
  const activeConversation = props.conversations.find((conversation) => conversation.id === props.activeConversationId) || null;

  return (
    <div className="fixed inset-0 z-[120]">
      <button className="absolute inset-0 bg-slate-900/45 backdrop-blur-[1px]" onClick={props.onClose} aria-label={ts('Close Sentinel')} />
      <section className="absolute left-1/2 top-1/2 w-[96vw] md:w-[92vw] max-w-[1040px] h-[92vh] md:h-[86vh] max-h-[860px] -translate-x-1/2 -translate-y-1/2 rounded-[28px] border border-slate-200 bg-[#fafafa] shadow-2xl flex flex-col overflow-hidden">
        <header className="h-16 px-5 border-b border-slate-200/80 bg-white/75 backdrop-blur flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-xl bg-ink text-white flex items-center justify-center shrink-0">
              <MessageCircle size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-semibold text-ink truncate">{ts('Sentinel Chat')}</p>
              <p className="text-xs text-slate-500 truncate">
                {activeConversation ? `${activeConversation.scope_type} ${activeConversation.scope_id || 'global'}` : ts('New conversation')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => void props.onNewChat()}
            >
              <MessageSquarePlus size={13} />
              {ts('New chat')}
            </button>
            <button className="h-8 w-8 rounded-full border border-slate-300 bg-white text-slate-500 hover:text-ink inline-flex items-center justify-center" onClick={props.onClose} aria-label={ts('Close Sentinel')}>
              <X size={15} />
            </button>
          </div>
        </header>

        <div className="px-4 md:px-6 py-3 border-b border-slate-200/70 bg-white/60">
          <div className="flex flex-wrap items-center gap-2">
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">{effectiveContext.module}</span>
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">{effectiveContext.user_role || ts('role-unavailable')}</span>
            <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">{effectiveContext.tenant_id || ts('tenant-unavailable')}</span>
            {selectedEntityChips.map((chip) => (
              <span key={chip} className="px-2 py-1 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-xs">
                {chip}
              </span>
            ))}
            {props.loading ? <span className="text-xs text-slate-500">{ts('Syncing...')}</span> : null}
          </div>
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
            {props.conversations.slice(0, 12).map((conversation) => {
              const active = props.activeConversationId === conversation.id;
              return (
                <button
                  key={conversation.id}
                  onClick={() => props.onSelectConversation(conversation.id)}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border text-xs transition ${
                    active ? 'border-ink bg-ink text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                  title={conversation.scope_id || 'Global'}
                >
                  <Clock3 size={12} />
                  {conversation.scope_type}:{conversation.scope_id || 'global'}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 overflow-auto px-4 md:px-6 py-5 space-y-4 bg-gradient-to-b from-[#f6f7fb] to-[#f9fafb]">
          {!props.messages.length ? (
            <div className="max-w-2xl mx-auto rounded-3xl border border-slate-200 bg-white p-6 text-sm text-slate-600">
              <div className="flex items-center gap-2 text-ink font-semibold mb-2">
                <Sparkles size={15} className="text-sky-600" />
                {ts('Start a conversation')}
              </div>
              <p>{ts('Ask for chart summaries, action proposals, scheduling support, or administrative guidance based on your current context.')}</p>
            </div>
          ) : null}
          {props.messages.map((message) => (
            <article key={message.id} className={`max-w-3xl ${message.role === 'user' ? 'ml-auto' : 'mr-auto'}`}>
              <div className={`flex items-start gap-2 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div
                  className={`h-7 w-7 rounded-full shrink-0 inline-flex items-center justify-center ${
                    message.role === 'user'
                      ? 'bg-ink text-white'
                      : message.role === 'assistant'
                        ? 'bg-sky-100 text-sky-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}
                >
                  {message.role === 'user' ? <User2 size={13} /> : message.role === 'assistant' ? <Bot size={13} /> : <ShieldAlert size={13} />}
                </div>
                <div
                  className={`rounded-3xl px-4 py-3 border ${
                    message.role === 'user'
                      ? 'bg-ink text-white border-ink'
                      : message.role === 'system'
                        ? 'bg-amber-50 border-amber-200 text-amber-900'
                        : 'bg-white border-slate-200 text-slate-800'
                  }`}
                >
                  <p className="text-[11px] uppercase tracking-wide opacity-70">{message.role === 'user' ? ts('User') : message.role === 'assistant' ? ts('Assistant') : ts('System')}</p>
                  <p className="text-sm whitespace-pre-wrap mt-1 leading-relaxed">{message.content || (props.sending && message.role === 'assistant' ? ts('Thinking...') : '')}</p>
                </div>
              </div>
            </article>
          ))}

          {props.proposals
            .filter((proposal) => proposal.status === 'pending')
            .map((proposal) => (
              <div key={proposal.id} className="max-w-3xl rounded-3xl border border-sky-200 bg-sky-50 p-4 mr-auto">
                <div className="flex items-center gap-2 text-sky-800">
                  <ShieldAlert size={15} />
                  <p className="font-semibold text-sm">{ts('Proposed Action')}: {proposal.action_type}</p>
                </div>
                <pre className="text-xs text-slate-700 mt-3 bg-white border border-slate-200 rounded-xl p-3 overflow-auto">
{JSON.stringify(
  {
    entity_scope: proposal.entity_scope,
    payload: proposal.payload,
  },
  null,
  2,
)}
                </pre>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs"
                    onClick={() => void props.onConfirmProposal(proposal.id)}
                  >
                    <Check size={12} />
                    {ts('Confirm')}
                  </button>
                  <button
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 text-xs text-slate-700 bg-white"
                    onClick={() => void props.onCancelProposal(proposal.id)}
                  >
                    <X size={12} />
                    {ts('Cancel')}
                  </button>
                </div>
              </div>
            ))}
        </div>

        <footer className="px-4 md:px-6 pb-5 pt-3 border-t border-slate-200/80 bg-white">
          {composer.startsWith('/') ? (
            <div className="mb-3 border border-slate-200 rounded-2xl bg-white max-h-40 overflow-auto">
              {filteredCommands.map((item) => (
                <button
                  key={item.command}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
                  onClick={() => {
                    setComposer(item.command);
                    setCommandFilter(item.command);
                  }}
                >
                  <span className="font-semibold text-ink">{item.command}</span>
                  <span className="text-slate-500 ml-2">{item.hint}</span>
                </button>
              ))}
            </div>
          ) : null}
          <form onSubmit={submitComposer} className="rounded-[26px] border border-slate-300 bg-white px-3 py-2 shadow-sm">
            <textarea
              ref={composerRef}
              className="w-full min-h-[64px] max-h-48 bg-transparent text-[15px] text-slate-800 placeholder:text-slate-400 focus:outline-none resize-none px-1 py-1"
              placeholder={ts('Ask Sentinel anything...')}
              value={composer}
              onChange={(event) => {
                setComposer(event.target.value);
                if (event.target.value.startsWith('/')) {
                  setCommandFilter(event.target.value);
                } else {
                  setCommandFilter('');
                }
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  if (!props.sending && composer.trim()) {
                    void submitComposer(event as unknown as FormEvent);
                  }
                }
              }}
            />
            <div className="mt-2 flex items-center justify-between">
              <p className="text-[11px] text-slate-500">{ts('Sentinel proposes actions first and executes only after confirmation.')}</p>
              <button
                type="submit"
                disabled={props.sending || !composer.trim()}
                className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-indigo-600 text-white disabled:opacity-50"
              >
                <Send size={14} />
              </button>
            </div>
          </form>
        </footer>
      </section>
    </div>
  );
}
